import {
  buildIngestComposioConfigForEvent,
  coerceSchemaFieldValue,
  extractIngestSchemaFieldsForEvent,
  filterSlugsForIngestField,
  getConnectionToolkitSlug,
  getTriggerTypeEventName,
  getTriggerTypeLabel,
  getTriggerTypeSlug,
  parseComposioConfigObject,
} from '../../utils/composioTrigger';

export const GOOGLE_DRIVE_FILE_CREATED_SLUG = 'GOOGLEDRIVE_FILE_CREATED_TRIGGER';

export const formatEventSlug = (slug = '') =>
  String(slug)
    .replace(/_/g, ' ')
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase());

export const isFileIngestToolkit = (toolkitSlug) =>
  String(toolkitSlug || '')
    .toLowerCase()
    .replace(/[\s_-]/g, '')
    .includes('googledrive');

export const FILE_INGEST_TOOLKIT_LABELS = ['Google Drive'];

export const buildGoogleDriveSyncTriggerName = (folderName, folderId) => {
  const label = String(folderName || '').trim() || `Folder ${folderId}`;
  return `${label} — Google Drive sync`;
};

export const isGoogleDriveConnection = (conn) => {
  if (!conn) return false;
  if (isFileIngestToolkit(getConnectionToolkitSlug(conn))) return true;
  const appName = String(
    conn.app_name || conn.appName || conn.metadata?.app_name || '',
  ).toLowerCase();
  return appName.includes('googledrive') || appName.includes('google drive');
};

export const listGoogleDriveConnections = (connections = []) =>
  connections.filter((conn) => {
    if (!isGoogleDriveConnection(conn)) return false;
    const status = conn.status || conn.connection_status;
    return status === 'active' || status === 'connected';
  });

export const resolveGoogleDriveConnection = (connections = []) =>
  listGoogleDriveConnections(connections)[0] || null;

export const pickGoogleDriveFileCreatedSlug = (triggerTypes = []) => {
  const match = triggerTypes.find((item) => {
    const slug = getTriggerTypeSlug(item).toUpperCase();
    const label = getTriggerTypeLabel(item).toLowerCase();
    return slug.includes('FILE_CREATED') || label.includes('file created');
  });
  return match ? getTriggerTypeSlug(match) : GOOGLE_DRIVE_FILE_CREATED_SLUG;
};

export const defaultTriggerForm = {
  intent: 'agent',
  agent_id: '',
  name: '',
  trigger_type: 'webhook',
  query_template: 'Summarize and act on this payload:\n\n{{payload}}',
  require_signature: false,
  connected_account_id: '',
  composio_trigger_slug: '',
  composio_trigger_slugs: [],
  composio_trigger_config_json: '{}',
  composio_config_values: {},
  composio_config_by_event: {},
  drive_folder_id: '',
  trigger_config_json: '',
  ingest_to_dim3: false,
  dim3_folder_id: '',
  mirror_nested: false,
};

export const createIngestFormState = (dim3FolderId = '') => ({
  ...defaultTriggerForm,
  intent: 'ingest',
  trigger_type: 'composio',
  ingest_to_dim3: true,
  query_template: '',
  dim3_folder_id: dim3FolderId === '' || dim3FolderId === null || dim3FolderId === undefined
    ? ''
    : String(dim3FolderId),
});

export const parseComposioTriggerConfig = (raw) => {
  const trimmed = String(raw || '').trim() || '{}';
  try {
    const parsed = JSON.parse(trimmed);
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
      throw new Error('Trigger configuration must be a JSON object.');
    }
    return parsed;
  } catch (e) {
    if (e.message?.includes('JSON object')) throw e;
    throw new Error('Trigger configuration must be valid JSON.');
  }
};

export const buildComposioTriggerPayload = (form, schemaFields = []) => {
  if (!form.connected_account_id) {
    throw new Error('Select a connected Composio app account.');
  }

  const singleSlug = String(form.composio_trigger_slug || '').trim();
  if (!singleSlug) {
    throw new Error('Select a Composio event type.');
  }

  let composio_trigger_config = {};
  if (schemaFields.length > 0) {
    const values = form.composio_config_values || {};
    schemaFields.forEach((field) => {
      const raw = values[field.key];
      if (raw === undefined || raw === '') {
        if (field.required) {
          throw new Error(`"${field.label}" is required for this event type.`);
        }
        return;
      }
      composio_trigger_config[field.key] = coerceSchemaFieldValue(raw, field.type);
    });
  } else {
    composio_trigger_config = parseComposioTriggerConfig(form.composio_trigger_config_json);
  }

  return {
    composio_trigger_slug: singleSlug,
    connected_account_id: form.connected_account_id,
    composio_trigger_config,
  };
};

export const buildIngestTriggerConfigForSlug = (
  form,
  slug,
  composioTypeSchemas,
  configByEvent = {},
) => {
  const schemaData = composioTypeSchemas[slug];
  if (!schemaData) {
    throw new Error('Event configuration is still loading. Please wait and try again.');
  }

  const eventName = getTriggerTypeEventName(schemaData, formatEventSlug(slug));
  const setupFields = extractIngestSchemaFieldsForEvent(schemaData, slug);
  const values = configByEvent[slug] || {};

  setupFields
    .filter((field) => field.required)
    .forEach((field) => {
      const raw = values[field.key];
      if (raw === undefined || String(raw).trim() === '') {
        throw new Error(`"${field.label}" is required for ${eventName}.`);
      }
    });

  const composio_trigger_config = buildIngestComposioConfigForEvent(
    schemaData,
    values,
    setupFields,
  );

  const payload = {
    composio_trigger_slug: slug,
    connected_account_id: form.connected_account_id,
    composio_trigger_config,
    ingest_to_dim3: true,
    dim3_folder_id: Number(form.dim3_folder_id),
  };

  if (form.mirror_nested) {
    const folderId = composio_trigger_config.folder_id;
    if (folderId !== undefined && folderId !== null && String(folderId).trim() !== '') {
      payload.mirror_nested = true;
    }
  }

  return payload;
};

export const apiErrMessage = (err, fallback) =>
  err?.response?.data?.detail
    ? typeof err.response.data.detail === 'string'
      ? err.response.data.detail
      : JSON.stringify(err.response.data.detail)
    : err?.message || fallback;

export { filterSlugsForIngestField, parseComposioConfigObject };

export const readMergedIngestFieldValue = (configByEvent = {}, slugs = [], key) => {
  for (const slug of slugs) {
    const value = configByEvent?.[slug]?.[key];
    if (value !== undefined && value !== '') return value;
  }
  return '';
};

export const writeMergedIngestFieldValue = (configByEvent = {}, slugs = [], key, value) => {
  const next = { ...configByEvent };
  slugs.forEach((slug) => {
    next[slug] = { ...(next[slug] || {}), [key]: value };
  });
  return next;
};
